/**
 * Shared mutable ref for the Event Graph nodes and edges.
 * BlueprintEditor writes here on every change.
 * EventRuntime reads here when an event fires.
 *
 * Change subscription: callers that need to react to graph edits (e.g. to
 * stop voxy when its config node is removed) can subscribeEventGraph() and
 * mutators must call notifyEventGraphChange() after reassigning nodes/edges.
 */
export const eventGraphState = {
  nodes: [],
  edges: [],
  functions: {}, // { [functionId]: { id, name, inputs, outputs, nodes, edges } }
}

// Live-state debug handle (parallels window.__SM_RUNTIME__ / __VARIABLE_VALUES__).
// Read by tests + the console to inspect or inject the event graph; mutators
// must call notifyEventGraphChange() after reassigning nodes/edges.
if (typeof window !== 'undefined') window.__EVENT_GRAPH__ = eventGraphState

const _subs = new Set()

/** Subscribe to graph mutations. Returns an unsubscribe function. */
export function subscribeEventGraph(cb) {
  _subs.add(cb)
  return () => _subs.delete(cb)
}

/** Mutators call this after updating eventGraphState.nodes / .edges. */
export function notifyEventGraphChange() {
  for (const cb of _subs) {
    try { cb() } catch (e) { console.warn('[eventGraphState] subscriber threw:', e) }
  }
}

// Test/console seam — mirrors window.__EVENT_GRAPH__ above. Lets an e2e simulate the
// "editor applied an edit" notification (BlueprintEditor.jsx, loadBlueprint.js) after
// mutating window.__EVENT_GRAPH__ directly, without driving the full editor UI.
if (typeof window !== 'undefined') window.__NOTIFY_EVENT_GRAPH_CHANGE__ = notifyEventGraphChange

// ── External-insert channel ──────────────────────────────────────────────────
// The editor's React state is the source that flows DOWN into eventGraphState (EventGraph /
// BlueprintEditor sync effects); there is no reverse path. So a node pushed straight onto
// eventGraphState.nodes from outside the editor (CameraRigNotice's live "Add Orbit Rig" fix)
// is invisible to the editor AND gets clobbered by its next down-sync. This channel closes
// that gap: it carries the inserted node so the editor can merge exactly it into React state
// (append, dedup by id) — never a broad reconcile, which would be unsafe mid character-switch.
const _insertSubs = new Set()

/** Subscribe to external node inserts. Returns an unsubscribe function. */
export function subscribeEventGraphInsert(cb) {
  _insertSubs.add(cb)
  return () => _insertSubs.delete(cb)
}

/** Announce a node inserted into the top-level graph from outside the editor. */
export function notifyEventGraphInsert(node) {
  for (const cb of _insertSubs) {
    try { cb(node) } catch (e) { console.warn('[eventGraphState] insert subscriber threw:', e) }
  }
}
